/**
 * useKeyboardShortcut Hook
 *
 * Listens for keyboard shortcuts (Cmd+K / Ctrl+K) and triggers callback
 * Handles cleanup on unmount
 */

import { useEffect } from 'react';

export interface UseKeyboardShortcutOptions {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  preventDefault?: boolean;
}

/**
 * Hook to listen for keyboard shortcuts
 *
 * @param callback - Function to call when shortcut is triggered
 * @param options - Keyboard shortcut configuration
 */
export const useKeyboardShortcut = (
  callback: () => void,
  options: UseKeyboardShortcutOptions
): void => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.key || !options.key) return;
      // Check if the key matches
      const keyMatches = event.key.toLowerCase() === options.key.toLowerCase();

      // Check modifier keys
      const metaMatches = options.metaKey === undefined || event.metaKey === options.metaKey;
      const ctrlMatches = options.ctrlKey === undefined || event.ctrlKey === options.ctrlKey;
      const shiftMatches = options.shiftKey === undefined || event.shiftKey === options.shiftKey;
      const altMatches = options.altKey === undefined || event.altKey === options.altKey;

      // Don't trigger if user is typing in an input field
      const target = event.target as HTMLElement;
      const isInputField =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (keyMatches && metaMatches && ctrlMatches && shiftMatches && altMatches && !isInputField) {
        if (options.preventDefault !== false) {
          event.preventDefault();
        }
        callback();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [callback, options]);
};

/**
 * Convenience hook for Cmd+K / Ctrl+K shortcut
 * Works on both Mac (Cmd+K) and Windows/Linux (Ctrl+K)
 */
export const useCmdK = (callback: () => void): void => {
  useKeyboardShortcut(callback, {
    key: 'k',
    metaKey: navigator.platform.toLowerCase().includes('mac'),
    ctrlKey: !navigator.platform.toLowerCase().includes('mac'),
    preventDefault: true,
  });
};

export default useKeyboardShortcut;
